import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Moon, AlertTriangle, Package } from 'lucide-react';
import { useNavigate } from 'react-router';

export function WarrantyMaintenance() {
  const navigate = useNavigate();
  const tips = [
    {
      text: "Maintain regular 6-month cleanings and exams",
      icon: <Calendar size={20} />,
      color: "from-teal-500 to-teal-400"
    },
    {
      text: "Follow all post-treatment care instructions",
      icon: <CheckCircle size={20} />,
      color: "from-teal-500 to-green-500"
    },
    {
      text: "Wear night guard if prescribed",
      icon: <Moon size={20} />,
      color: "from-green-500 to-teal-500"
    },
    {
      text: "Avoid using teeth to open packaging or bottles",
      icon: <Package size={20} />,
      color: "from-green-500 to-green-400"
    },
    {
      text: "Report discomfort or damage early",
      icon: <AlertTriangle size={20} />,
      color: "from-teal-500 to-teal-400"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: { 
      x: 0, 
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section className="bg-white py-16 md:py-24 px-6 relative overflow-hidden">
      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div 
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-block mb-4">
            <motion.div 
              className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-teal-500 to-green-500 flex items-center justify-center shadow-lg"
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.div>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="h-1 w-24 bg-gradient-to-r from-teal-500 to-green-500 rounded-full mx-auto mt-4"
            ></motion.div>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            How to 
            <span className="bg-gradient-to-r from-teal-600 to-green-600 text-transparent bg-clip-text"> Maintain </span> 
            Your Warranty
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Follow these important guidelines to ensure your dental treatments remain covered under our warranty program.
          </p>
        </motion.div>

        <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-3xl p-8 shadow-lg">
          <motion.ul 
            className="space-y-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {tips.map((tip, i) => (
              <motion.li 
                key={i}
                className="flex items-start gap-4 group"
                variants={itemVariants}
                whileHover={{ x: 5 }}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r ${tip.color} flex items-center justify-center shadow-md text-white transform transition-transform group-hover:scale-110`}>
                  {tip.icon}
                </div>
                <div className="flex-1">
                  <p className="text-gray-700 font-medium">{tip.text}</p>
                  <div className="mt-2 h-px w-full bg-gradient-to-r from-teal-200 to-green-200 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        <motion.div 
          className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4 p-6 bg-white rounded-xl border border-teal-100 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <div className="bg-teal-100 p-3 rounded-full">
            <svg className="w-6 h-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-center md:text-left">
            <p className="text-gray-700">
              <span className="font-semibold">Pro Tip:</span> Schedule your next cleaning today to ensure continuous warranty coverage!
            </p>
          </div>
          <motion.button 
            onClick={() => navigate('/schedule-appointment')}
            className="cursor-pointer px-6 py-2 bg-gradient-to-r from-teal-500 to-green-500 text-white rounded-full shadow hover:shadow-lg transform transition hover:-translate-y-1"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Book Now
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}

export default WarrantyMaintenance;